/**
 * Renders flash messages from JavaScript into the header's #flash-area as the
 * same full-width banner app/views/shared/_flash_messages.html.erb renders for
 * server-side flashes, so a page that reports results over fetch (checkout,
 * fees) looks and behaves like the rest of the app.
 *
 * Each message becomes one card with a colour bar, the text and a Dismiss
 * button, driven by the `flash` Stimulus controller. Several messages stack
 * vertically in one fixed container, so a long or second message cannot spill
 * over the header the way the old inline pills did.
 *
 * Notices fade after the timeout the layout puts on #flash-area
 * (Rails.configuration.x.flash_timeout_ms). Alerts and warnings ask the user
 * to do something (retry a declined card, print a receipt by hand), so they
 * stay until dismissed.
 */

const STYLES = {
  notice: { bar: "bg-emerald-400", body: "bg-emerald-50 text-emerald-700", button: "text-emerald-700 hover:bg-emerald-100 focus-visible:outline-emerald-600", role: "status" },
  warning: { bar: "bg-amber-400", body: "bg-amber-50 text-amber-800", button: "text-amber-800 hover:bg-amber-100 focus-visible:outline-amber-600", role: "status" },
  alert: { bar: "bg-rose-400", body: "bg-rose-50 text-rose-700", button: "text-rose-700 hover:bg-rose-100 focus-visible:outline-rose-600", role: "alert" }
}

const PERSISTENT_TIMEOUT = "0"

/**
 * @param {Array<{type: "notice"|"warning"|"alert", text: string}>} messages
 *   shown top to bottom; an unrecognised type is shown as an alert
 */
export function showFlash(messages) {
  const flashArea = document.querySelector("#flash-area")
  if (!flashArea) return

  const stack = document.createElement("div")
  stack.className = "pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col gap-1"
  messages.forEach(message => {
    stack.appendChild(flashCard(message, flashArea.dataset.flashTimeout))
  })

  flashArea.textContent = ""
  flashArea.appendChild(stack)
}

function flashCard({ type, text }, configuredTimeout) {
  const style = STYLES[type] || STYLES.alert
  const timeout = type === "notice" ? configuredTimeout : PERSISTENT_TIMEOUT

  const card = document.createElement("div")
  card.className = "pointer-events-auto overflow-hidden rounded-b-lg bg-white shadow-sm ring-1 ring-black/5"
  card.setAttribute("role", style.role)
  card.dataset.controller = "flash"
  if (timeout !== undefined) card.dataset.flashTimeout = timeout

  const bar = document.createElement("div")
  bar.className = `h-1 ${style.bar}`
  card.appendChild(bar)
  card.appendChild(flashBody(text, style))
  return card
}

function flashBody(text, style) {
  const body = document.createElement("div")
  body.className = `flex items-start gap-4 px-4 py-3 ${style.body}`

  const paragraph = document.createElement("p")
  paragraph.className = "min-w-0 flex-1 text-pretty text-base/7 sm:text-sm/6"
  paragraph.textContent = text
  body.appendChild(paragraph)
  body.appendChild(dismissButton(style))
  return body
}

// m-0 and the text colour override application.scss, which styles every bare
// <button> white with a 2rem margin.
function dismissButton(style) {
  const button = document.createElement("button")
  button.type = "button"
  button.className = `m-0 shrink-0 rounded-md px-2 py-1 text-base/7 font-medium sm:text-sm/6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${style.button}`
  button.dataset.action = "click->flash#dismiss"
  button.textContent = "Dismiss"
  return button
}
